// What the sender of a quick line can name (R1607, R1608): the one reader
// of the state store and the fleet layer that the quick wheel asks when it
// opens. The answer is a preview and a gate; the hub resolves every
// position again from its own state.
//
// - The marker is the sender's NEWEST live map marker: the list is oldest
//   first, and a marker whose life ended is gone for the hub too.
// - The hold is the sender's own player base.
// - The fleet is the selected own fleet; the contact is the selected fleet
//   of another faction. A selection of a faction mate's fleet is neither.
// - The object is the strategic object on the cell of the newest marker,
//   with the world-clock hour of its window from the public siege frame.

import { SiteKind, type MapMarker, type SiegeInfo, type Site } from '../gen/proto/aquaphobia';
import type { Selection } from '../state';
import type { QuickCtx } from '../ui/quickchat';
import { cellAt } from '../world';

/** What the context reads of the state store. `GameState` satisfies it. */
export interface QuickCtxState {
  readonly mapMarkers: readonly MapMarker[];
  readonly playerId: number;
  readonly faction: number;
  readonly selection: Selection;
  readonly sites: ReadonlyMap<number, Site>;
  readonly sieges: readonly SiegeInfo[];
}

/** What the context reads of the fleet layer. `FleetLayer` satisfies it. */
export interface QuickCtxFleets {
  describe(id: number): { own: boolean; faction: number; name: string } | null;
  at(id: number): { x: number; y: number } | null;
}

/** The sender's context at one wall second. */
export function quickCtxOf(s: QuickCtxState, fleets: QuickCtxFleets, nowSec: number): QuickCtx {
  let marker: QuickCtx['marker'] = null;
  for (let i = s.mapMarkers.length - 1; i >= 0; i--) {
    const m = s.mapMarkers[i];
    if (m.ownerId === s.playerId && (m.expiresUnix === 0 || m.expiresUnix > nowSec)) {
      marker = { cell: m.cell };
      break;
    }
  }
  let hold: QuickCtx['hold'] = null;
  for (const site of s.sites.values()) {
    if (site.kind === SiteKind.SITE_KIND_PLAYER_BASE && site.ownerPlayer === s.playerId) {
      hold = { siteId: site.id, cell: site.cell, name: site.name };
      break;
    }
  }
  let fleet: QuickCtx['fleet'] = null;
  let contact: QuickCtx['contact'] = null;
  const sel = s.selection;
  if (sel?.kind === 'fleet') {
    const f = fleets.describe(sel.id);
    const at = fleets.at(sel.id);
    const cell = at ? cellAt(at.x, at.y) : -1;
    if (f && cell >= 0) {
      if (f.own) fleet = { id: sel.id, cell, name: f.name };
      else if (f.faction !== s.faction) contact = { id: sel.id, cell };
    }
  }
  let object: QuickCtx['object'] = null;
  if (marker) {
    const cell = marker.cell;
    const o = s.sieges.find((x) => x.cell === cell);
    if (o) object = { siteId: o.siteId, cell: o.cell, name: o.siteName, hour: o.windowHour };
  }
  return { marker, hold, fleet, contact, object };
}
